import RemoteChart from '@webtrader-chart/remote';
import { Time } from 'lightweight-charts';
import debounce from 'lodash/debounce';

import { IApiError } from '@frontend/request';

import { EChartPeriod, ILatestChartTime, IMarketChartData, chartAPIController } from '@aqx-core/common/api/chart';
import { EMainProductCodeType } from '@aqx-core/common/api/config';
import { ISymbol } from '@aqx-core/common/api/market';
import { ENotificationOrderStateCentroid, INotificationMetadata } from '@aqx-core/common/api/notification';
import { EOrderType, IOrder, IPendingOrder } from '@aqx-core/common/api/order';
import {
  EChartIndicator,
  EUserPreferenceCategory,
  EUserPreferenceType,
  ITechnicalIndicator,
  userAccountSettingAPIController
} from '@aqx-core/common/api/user-account-setting';
import { checkIsOpenPosition } from '@aqx-core/common/calculation/order';
import dayjs from '@aqx-core/common/dayjs';
import { TAppLanguageISOCountryCode, getUserLanguage, i18n } from '@aqx-core/common/i18n';
import { mapResponseCodeToMessage } from '@aqx-core/common/request/message';
import { companyService } from '@aqx-core/common/services/company';
import { companyViewController } from '@aqx-core/common/services/company/controller';
import { mixpanelAnalysisService } from '@aqx-core/common/services/mixpanel';
import { orderListingService } from '@aqx-core/common/services/order';
import { SYM_SUB_KEY_UICHART, chartTopicSubscription, socketService } from '@aqx-core/common/socket';
import { CHART_MESSAGE_TYPE, IChartTopicMessage } from '@aqx-core/common/socket/topics/chart';
import storage, { EStorageKey } from '@aqx-core/common/storage';
import {
  DEFAULT_CANDLE_STICK_COLORS_RGB,
  EAppThemeMode,
  IAppThemeColors,
  appThemeService,
  getThemeColors
} from '@aqx-core/common/theme';
import { chartPeriodToTVPeriod, periodToSeconds } from '@aqx-core/common/utils/chart';
import { destructureAsNumber, safetyCheckForNumberValues } from '@aqx-core/common/utils/number';
import { getOrderBuySellType, isOrderClosableOrEditable, mapOrderTypeToText } from '@aqx-core/common/utils/order';
import { trimToDecimal } from '@aqx-core/common/utils/price/trimToDecimal';

import { chartMainAndSubIndicators, chartMainIndicators } from './constants/indicator';
import { equityChartService } from './service';
import {
  EChartProvider,
  EEventMessageType,
  IGetEntryAndAdditionalPricesParams,
  IGetEntryPriceInfoParams,
  IOrderTracker,
  IPostEventMessage,
  IPostEventUpdateIndicatorFormMessage,
  IReceiveEventMessage,
  IUpdateIndicatorParams
} from './types';
import { ICandlestickColorUserPreference } from './types/candlestick';
import { ICustomBarData } from './types/chart';
import { EChartSeries } from './types/series';
import { IRevertEditTrackOrderPriceMessageData, IUpdateTrackOrderPriceParams } from './types/track';
import { periodToDates, rawToBarData, trimBarData } from './utils';

interface IRetrackOrdersParams {
  orders: IOrder[];
  pendingOrders: IPendingOrder[];
  updateTrackers?: boolean;
}
class EquityChartViewController {
  private queuedEvents: Record<string, IPostEventMessage> = {};
  private remoteChart?: RemoteChart;
  private iFrame?: HTMLIFrameElement;
  private isChartReady?: boolean;
  private updateTechnicalIndicatorAbortController = new AbortController();
  private isChartLoading?: boolean;

  public get orderTrackers() {
    return equityChartService.context.orderTrackers;
  }

  private get technicalIndicators() {
    return equityChartService.context.technicalIndicators;
  }

  private selectedSymbol?: ISymbol;
  private previousSelectedSymbol?: ISymbol;

  private barData: ICustomBarData[] = [];
  // private previousAccessedBarData: ICustomBarData[] = [];

  constructor() {}

  public getSelectedSymbol = () => {
    return this.selectedSymbol;
  };

  public handleChartStatus = () => {
    this.isChartReady = true;
    this.dispatchEvent({
      type: EEventMessageType.ASK_PRICE_VISIBLE,
      data: equityChartService.context.askPriceVisible
    });
    this.dispatchQueuedEvents();
  };

  public getSeries = () => {
    return equityChartService.context.selectedSeries;
  };

  public setRemoteChart = (remoteChart: RemoteChart | undefined) => {
    this.remoteChart = remoteChart;
    this.setOriginalChartTheme();
  };

  private checkSelectedProvider = (provider: EChartProvider) => {
    return equityChartService.getSelectedProvider() === provider;
  };

  public reset = () => {
    this.resetChartReadyStatus();
    this.cleanAllOrderTrackers();
    equityChartService.setTechnicalIndicators([]);

    if (this.selectedSymbol) {
      this.setSymbol(this.selectedSymbol);
    }
    this.setRemoteChart(undefined);
  };

  public resetChartReadyStatus = () => {
    this.isChartReady = false;
    this.isChartLoading = false;
  };

  public setSymbol = async (symbol: ISymbol, shouldLoadLineDrawing?: boolean) => {
    if (this.checkSelectedProvider(EChartProvider.ORIGINAL)) {
      this.isChartLoading = true;
      this.dispatchEvent({
        type: EEventMessageType.CHART_LOAD,
        data: true
      });

      this.dispatchEvent({
        type: EEventMessageType.SET_SYMBOL,
        data: {
          symbol: symbol.symbol
        }
      });
      if (shouldLoadLineDrawing) {
        await this.getLineDrawingUserPreference(symbol.symbol);
      }
      this.selectedSymbol = symbol;
      await this.fetchChartLatestData(symbol.symbol);
    } else {
      this.selectedSymbol = symbol;
      this.setTvInitialConfigs();
      this.dispatchEvent({
        type: EEventMessageType.SET_SYMBOL,
        data: {
          symbol: symbol.symbol,
          metadata: {
            ...symbol
          }
        }
      });
    }
  };

  private dispatchQueuedEvents = () => {
    Object.values(this.queuedEvents).forEach((event) => {
      this.dispatchEvent(event);
    });

    // Clear the queued events
    this.queuedEvents = {};
  };

  private dispatchEvent = (event: IPostEventMessage, directPost?: boolean) => {
    const { remoteChart, isChartReady, iFrame } = this;

    if (isChartReady || directPost) {
      // Module federation (Original Chart Web)
      if (remoteChart) {
        remoteChart.dispatchEvent(event);
      }

      // iFrame (Trading View Web)
      if (iFrame) {
        iFrame.contentWindow?.postMessage(event, '*');
      }
    } else {
      let queuedEventKey: string = event.type;
      //* Handle indicator events that are triggered when the chart is not yet ready, but multiple indicators are being initialized.
      if (event.type === EEventMessageType.INDICATOR) {
        queuedEventKey = `${event.type}-${event.data}`;
      }

      this.queuedEvents[queuedEventKey] = {
        ...event
      };
    }
  };

  public getLineDrawingUserPreference = async (symbol?: string) => {
    await userAccountSettingAPIController.getUserPreference(
      {
        type: EUserPreferenceType.LINE_DRAWING,
        category: EUserPreferenceCategory.LINE_DRAWING,
        symbol
      },
      {
        onSuccess: (result) => {
          const lineDrawing =
            result?.find(
              (p) => p.category === EUserPreferenceCategory.LINE_DRAWING && p.type === EUserPreferenceType.LINE_DRAWING
            )?.value ?? '';

          if (lineDrawing && JSON.parse(lineDrawing).length > 0) {
            this.dispatchEvent({
              type: EEventMessageType.OPEN_DRAWING,
              data: lineDrawing
            });
          }
        },
        onError: () => {}
      }
    );
  };

  public getAllTechnicalIndicators = async (initialLoad?: boolean) => {
    equityChartService.setLoading(true);
    await userAccountSettingAPIController.getTechnicalIndicator(undefined, {
      onSuccess: (result) => {
        if (result && result.length > 0) {
          result.sort((a, b) => chartMainAndSubIndicators.indexOf(a.type) - chartMainAndSubIndicators.indexOf(b.type));
          equityChartService.setTechnicalIndicators(result || []);
          if (initialLoad) {
            this.initFirstLoadTechnicalIndicator(result);
          }
        }
      },
      onError: (error) => {
        const { code, message } = error as IApiError;
        throw new Error(mapResponseCodeToMessage(code, message));
      },
      onFinally: () => {
        equityChartService.setLoading(false);
      }
    });
  };

  public initFirstLoadTechnicalIndicator = (technicalIndicators: ITechnicalIndicator[]) => {
    if (technicalIndicators.length <= 0) {
      return;
    }

    for (const indicator of technicalIndicators) {
      if (!!indicator.enabled) {
        this.dispatchEvent({
          type: EEventMessageType.INDICATOR,
          data: indicator.type,
          needSort: true
        });
      }
    }
  };

  public onSelectIndicator = async (
    indicator: EChartIndicator,
    symbol?: string,
    onError?: (message: string) => void
  ) => {
    try {
      const technicalIndicators = this.technicalIndicators;

      const selectedIndicator = technicalIndicators.find((i) => i.type === indicator);
      const isIndicatorEnabled = !selectedIndicator?.enabled;

      const technicalIndicatorList: ITechnicalIndicator[] = [];
      technicalIndicatorList.push({ type: indicator, enabled: isIndicatorEnabled });
      await this.updateTechnicalIndicator({ technicalIndicatorList });

      const tempTechnicalIndicators = [...technicalIndicators];
      if (!selectedIndicator) {
        tempTechnicalIndicators.push({ type: indicator, enabled: true });
        equityChartService.setTechnicalIndicators(tempTechnicalIndicators);
      } else if (tempTechnicalIndicators.length > 0) {
        const index = tempTechnicalIndicators.findIndex((i) => i.type === indicator);
        tempTechnicalIndicators[index].enabled = !selectedIndicator?.enabled;

        equityChartService.setTechnicalIndicators(tempTechnicalIndicators);
      }

      this.dispatchEvent({
        type: EEventMessageType.INDICATOR,
        data: indicator
      });

      if (isIndicatorEnabled) {
        mixpanelAnalysisService.chart.indicatorSelect({
          symbol: symbol ?? '-',
          indicator: indicator?.toUpperCase()
        });
      }
    } catch (error) {
      const { code, message } = error as IApiError;
      onError?.(mapResponseCodeToMessage(code, message));
    }
  };

  public updateTechnicalIndicator = async (params: IUpdateIndicatorParams) => {
    const { technicalIndicatorList, onComplete } = params;
    equityChartService.setLoading(true);
    this.updateTechnicalIndicatorAbortController.abort();
    this.updateTechnicalIndicatorAbortController = new AbortController();
    await userAccountSettingAPIController.updateTechnicalIndicator(
      { technicalIndicatorList },
      {
        onSuccess: () => {
          onComplete?.();
        },
        onError: (error) => {
          throw error;
        },
        onFinally: () => {
          equityChartService.setLoading(false);
        }
      },
      this.updateTechnicalIndicatorAbortController.signal
    );
  };

  public onSelectPeriod = async (period: EChartPeriod) => {
    if (this.checkSelectedProvider(EChartProvider.TRADING_VIEW)) {
      this.dispatchEvent({
        type: EEventMessageType.TV_SET_PERIOD,
        data: chartPeriodToTVPeriod(period)
      });
      if (this.selectedSymbol) {
        const previousPeriod = equityChartService.context.selectedPeriod;
        this.unsubscribeChartTopic(this.selectedSymbol?.symbol, previousPeriod);

        equityChartService.setSelectedPeriod(period);
        this.subscribeChartTopic(this.selectedSymbol?.symbol, period);
      }
      return;
    }

    this.dispatchEvent({
      type: EEventMessageType.CHART_LOAD,
      data: true
    });

    if (this.selectedSymbol) {
      const previousPeriod = equityChartService.context.selectedPeriod;
      this.unsubscribeChartTopic(this.selectedSymbol?.symbol, previousPeriod);

      equityChartService.setSelectedPeriod(period);
      await this.fetchChartLatestData(this.selectedSymbol?.symbol);
      this.subscribeChartTopic(this.selectedSymbol?.symbol, period);
    }

    const eventMessage = {
      chartData: this.barData,
      period
    };

    this.dispatchEvent({
      type: EEventMessageType.PERIOD,
      data: eventMessage
    });
    this.updateOrderTrackers();
  };

  public onSelectChartProvider = (provider: EChartProvider) => {
    equityChartService.setSelectedProvider(provider);
    if (provider === EChartProvider.TRADING_VIEW) {
      this.setTvInitialConfigs();
    }
    this.onSelectSeries(equityChartService.context.selectedSeries);
  };

  public onSelectSeries = async (series: EChartSeries) => {
    this.dispatchEvent({
      type: EEventMessageType.CHART_LOAD,
      data: true
    });

    equityChartService.setSelectedSeries(series);

    if (series === EChartSeries.LINE_ASK) {
      // Remove all selected main indicators when selected series is line with ask price
      const technicalIndicatorList: ITechnicalIndicator[] = [];
      for (const indicator of chartMainIndicators) {
        technicalIndicatorList.push({
          type: indicator,
          enabled: false
        });
      }

      await this.clearAllTechnicalIndicators({ technicalIndicatorList });

      const technicalIndicators = this.technicalIndicators;

      for (const indicator of technicalIndicators) {
        const included = chartMainIndicators.find((i) => i === indicator.type);
        if (indicator.enabled && included) {
          this.dispatchEvent({
            type: EEventMessageType.INDICATOR,
            data: indicator.type
          });
        }
      }
    }
    this.dispatchEvent({
      type: EEventMessageType.SERIES,
      data: series
    });
  };

  public onToggleAskPriceVisible = () => {
    const newVisible = !equityChartService.context.askPriceVisible;
    storage.setItem(EStorageKey.ASK_PRICE_VISIBLE, newVisible);
    equityChartService.setAskPriceVisible(newVisible);
    this.dispatchEvent({
      type: EEventMessageType.ASK_PRICE_VISIBLE,
      data: newVisible
    });
  };

  public scrollToLatest = () => {
    this.dispatchEvent({
      type: EEventMessageType.SCROLL_TO_LATEST
    });
  };

  public clearAllTechnicalIndicators = async (params: IUpdateIndicatorParams) => {
    await this.updateTechnicalIndicator(params);
    this.getAllTechnicalIndicators();
  };

  public cleanAllOrderTrackers = () => {
    const trackersToRemove = this.orderTrackers;
    if (trackersToRemove && trackersToRemove.length > 0) {
      this.batchCancelTrackOrders(trackersToRemove.map((tracker) => tracker.orderId));
      equityChartService.setOrderTrackers([]);
    }
  };

  public resetAskPrice = () => {
    storage.setItem(EStorageKey.ASK_PRICE_VISIBLE, false);
    equityChartService.setAskPriceVisible(false);
    this.dispatchEvent({
      type: EEventMessageType.ASK_PRICE_VISIBLE,
      data: false
    });
  };

  public resetIndicator = async () => {
    const technicalIndicators = this.technicalIndicators;
    const technicalIndicatorList: ITechnicalIndicator[] = [];
    for (const indicator of technicalIndicators) {
      technicalIndicatorList.push({
        type: indicator.type,
        enabled: false
      });
    }
    await this.clearAllTechnicalIndicators({ technicalIndicatorList });
    for (const indicator of technicalIndicators) {
      if (indicator.enabled) {
        this.dispatchEvent({
          type: EEventMessageType.INDICATOR,
          data: indicator.type
        });
      }
    }
  };

  public resetDrawing = () => {
    this.dispatchEvent({
      type: EEventMessageType.REMOVE_DRAWING
    });
  };

  public initChartToken = () => {
    const token = storage.getItem(EStorageKey.TOKEN);
    this.dispatchEvent(
      {
        type: EEventMessageType.TOKEN,
        data: token
      },
      true
    );
  };

  public setLanguage = (language: TAppLanguageISOCountryCode) => {
    if (this.checkSelectedProvider(EChartProvider.TRADING_VIEW)) {
      this.setTVLanguage(language);
    } else {
      this.dispatchEvent({
        type: EEventMessageType.LANGUAGE,
        data: language
      });
    }
  };

  /* iFrame specific */
  public onReceiveIframeMessage = (
    event: IReceiveEventMessage
    // contextMenuRef?: RefObject<IUIContextMenuRef>
  ) => {
    switch (event.type) {
      case EEventMessageType.STATUS:
        if (event.data) {
          this.handleChartStatus();
        }
        break;
      // TODO: Implement for mobile original chart if cannot do modul federation approach for mobileapp
      // case EEventMessageType.OPEN_CONTEXT_MENU:
      //   contextMenuRef?.current?.open(event.data.x, event.data.y);
      //   break;
      // case EEventMessageType.INDICATOR:
      //   this.onSelectIndicator(event.data);
      //   break;
      // case EEventMessageType.CHART_FETCH_MORE:
      //   if (event.data) {
      //     const { latestFrom, latestTo } = event.data;
      //     this.fetchMoreChartData(latestFrom, latestTo);
      //   }
      //   break;
      default:
        break;
    }
  };

  /* Trading View Specific */

  public initTV = (iFrame: HTMLIFrameElement | undefined) => {
    const selectedSymbol = this.getSelectedSymbol();
    if (selectedSymbol) {
      this.setSymbol(selectedSymbol);
    }
    this.iFrame = iFrame;
    this.dispatchEvent({
      type: EEventMessageType.THEME_SCHEME,
      data: appThemeService.context.theme.themeColors
    });
  };

  private setTvInitialConfigs = () => {
    const period = chartPeriodToTVPeriod(equityChartService.context.selectedPeriod);
    const { positive, negative } = equityChartService.context.candlestickPreference;

    const data = {
      period,
      isServerTime: companyViewController.getMarketTimeIsServer(),
      serverUtcOffset: companyViewController.getUserServerTimezoneOffset(),
      localTimezone: dayjs.tz.guess(),
      languageCode: getUserLanguage() as TAppLanguageISOCountryCode,
      theme: appThemeService.context.theme.themeMode,
      series: this.getSeries(),
      candlestickColor: { positive, negative },
      backgroundColor: appThemeService.context.theme.themeColors.background.default
    };

    this.dispatchEvent({
      type: EEventMessageType.TV_SET_INITIAL_CONFIGS,
      data
    });
  };

  public getTradingViewUrl = () => {
    const TV_PATH = '/tv';
    // Implement different handling for local mobile development in the future
    return NODE_ENV === 'development' ? 'http://localhost:3007' : TV_PATH;
  };

  public toggleTVIndicators = () => {
    this.dispatchEvent({
      type: EEventMessageType.TV_TOGGLE_INDICATORS
    });
  };

  public setTVTheme = (theme: EAppThemeMode) => {
    this.dispatchEvent({
      type: EEventMessageType.TV_CHANGE_THEME,
      data: {
        theme,
        backgroundColor: appThemeService.context.theme.themeColors.background.default
      }
    });
  };

  private setTVLanguage = (language: TAppLanguageISOCountryCode) => {
    this.dispatchEvent({
      type: EEventMessageType.TV_SET_LANGUAGE,
      data: language
    });
  };

  public resetTV = () => {
    this.isChartReady = false;
    if (this.selectedSymbol) {
      this.setSymbol(this.selectedSymbol);
    }
    this.iFrame = undefined;
  };

  /** API call */
  // Chart Optimization Section
  // Main function to fetch bar data to post to chart
  public fetchChartLatestData = async (symbol: string, onComplete?: (isSuccess: boolean) => void) => {
    const { selectedPeriod } = equityChartService.context;
    const latestChart = await new Promise<ILatestChartTime | null | undefined>(async (resolve) => {
      chartAPIController.getLatestChart(
        { symbol, period: selectedPeriod },
        {
          onSuccess: async (latestChart) => {
            resolve(latestChart);
            // this.handleEndFetchChartData();
          },
          onError: (error) => {
            this.isChartLoading = false;
            if (!this.barData) {
              this.barData = [];
            }
            onComplete?.(false);
            if (!(error as IApiError).isAborted) {
              // this.handleEndFetchChartData();
              // this.contextValues.loading = false;
            }
            resolve(null);
          }
        }
      );
    });
    await this.fetchChartBarData(latestChart, selectedPeriod, symbol);
    this.isChartLoading = false;
    onComplete?.(true);
  };

  private fetchChartBarData = async (
    latestChart: ILatestChartTime | null | undefined,
    period: EChartPeriod,
    symbol: string
  ) => {
    const periodRange = periodToDates({
      period
    });

    let chartData: IMarketChartData[] | null | undefined = null;
    let latestBarData: IMarketChartData | null | undefined = null;

    await Promise.allSettled([
      chartAPIController.getHistoricalChartData(
        {
          symbol,
          period,
          from: periodRange.from,
          to: periodRange.to
        },
        {
          onSuccess: (response) => {
            chartData = response;
          },
          onError: () => {
            chartData = null;
          }
        }
      ),
      chartAPIController.getLatestBarData(
        {
          symbol,
          period,
          to: periodRange.to
        },
        {
          onSuccess: (response) => {
            latestBarData = response;
          },
          onError: () => {
            latestBarData = null;
          }
        }
      )
    ])
      .then(() => {
        if (chartData && latestBarData && symbol === this.selectedSymbol?.symbol) {
          let fullBarAPIData = chartData;
          // TODO - There might be a chance where the historical candlestick return the latest bar , in that case do not replace the historical data with the latestChartBar
          if (chartData[chartData.length - 1]?.chartTime !== latestBarData.chartTime) {
            fullBarAPIData = [...chartData, latestBarData];
          }

          const barData = rawToBarData(fullBarAPIData);

          if (barData?.length > 0 && equityChartService.context.selectedSeries !== EChartSeries.LINE_ASK) {
            barData[barData.length - 1].ask = latestChart?.ask;
          }

          const companyIntegration = companyService.getCompanyIntegration();

          this.barData = barData;
          this.dispatchEvent({
            type: EEventMessageType.SYMBOL_CHART_DATA,
            data: {
              barData,
              tickRulesGroup: this.selectedSymbol?.tickRulesGroup,
              decimals: this.selectedSymbol?.decimal,
              companyIntegration
            }
          });
        }

        this.handleChartTopicSubscriptions(symbol, period);
      })
      .catch(() => {});
  };

  public fetchMoreChartData = async (latestFrom: number, latestTo: number) => {
    const { selectedPeriod: period, fetchingMore } = equityChartService.context;
    const { barData } = this;
    const { symbol } = this.selectedSymbol || {};
    if (!symbol || fetchingMore) {
      return;
    }
    equityChartService.setFetchingMore(true);
    const { to, from } = periodToDates({
      period,
      latestFrom,
      latestTo,
      dateToUsesEndOfDay: false
    });
    try {
      await chartAPIController.getHistoricalChartData(
        {
          symbol,
          period,
          from,
          to
        },
        {
          onSuccess: (chartData) => {
            if (chartData && chartData.length > 0) {
              const trimNewBarData = trimBarData(rawToBarData(chartData), barData[0].time as number);
              const newBarDataWithPrevious = [...trimNewBarData, ...barData];
              this.barData = newBarDataWithPrevious;
              this.dispatchEvent({
                type: EEventMessageType.CHART_FEED,
                data: newBarDataWithPrevious
              });
            }
          },
          onError: (error) => {
            if (!(error as IApiError).isAborted) {
              equityChartService.setFetchingMore(false);
            }
          }
        }
      );
    } finally {
      setTimeout(() => {
        equityChartService.setFetchingMore(false);
      }, 500);
    }
  };

  public revertEditTrackOrderPrice = (data: IRevertEditTrackOrderPriceMessageData) => {
    this.dispatchEvent({
      type: EEventMessageType.REVERT_EDIT_TRACK_ORDER_PRICE,
      data
    });
  };

  public updateTrackOrderPrice = (params: IUpdateTrackOrderPriceParams) => {
    const { orderId, decimal, stopLoss, takeProfit, entryPrices } = params;

    this.dispatchEvent({
      type: EEventMessageType.UPDATE_TRACK_ORDER_PRICES,
      data: {
        orderId,
        stopLoss: trimToDecimal(stopLoss, decimal) ?? 0,
        takeProfit: trimToDecimal(takeProfit, decimal) ?? 0,
        entryPrices: entryPrices?.map((price) => trimToDecimal(price, decimal) ?? 0)
      }
    });
  };

  // For users accessing Trade page of same previous symbol
  // No need API call
  // Hide for now
  // public postExistingChartData = () => {
  //  const { activeSymbol, decimals, selectedPeriod } = this.context;
  //  const { barData, previousAccessedBarData} = this;
  //   const { selectedSymbol } = this;

  //   if (selectedSymbol) {
  //     // this.context.barData = previousAccessedBarData;

  //     this.dispatchEvent({
  //       type: EEventMessageType.SYMBOL_CHART_DATA,
  //       data: {
  //         barData: equityChartService.context.barData,
  //         tickRulesGroup: selectedSymbol?.tickRulesGroup
  //       }
  //     });

  //     this.handleChartTopicSubscriptions(selectedSymbol.symbol, equityChartService.context.selectedPeriod);
  //   }
  // };

  // Chart Section
  private handleChartTopicSubscriptions = async (symbol: string, period: EChartPeriod) => {
    const { previousSelectedSymbol } = this;

    if (previousSelectedSymbol?.symbol) {
      this.unsubscribeChartTopic(previousSelectedSymbol?.symbol, period);
    }

    // symbol topic subscription is handled by useSymbolTopic
    this.subscribeChartTopic(symbol, period);
    this.previousSelectedSymbol = this.selectedSymbol;
  };

  private subscribeChartTopic = async (symbol: string, period: EChartPeriod) => {
    chartTopicSubscription.subscribe(symbol, period, SYM_SUB_KEY_UICHART, this.onReceiveChartMessage);
  };

  private unsubscribeChartTopic = (symbol: string, period: EChartPeriod) => {
    chartTopicSubscription.unsubscribe(symbol, period, SYM_SUB_KEY_UICHART, this.onReceiveChartMessage);
  };

  private onReceiveChartMessage = (message: IChartTopicMessage | undefined) => {
    const isChartReady = this.isChartReady;
    const { symbol } = this.selectedSymbol || {};

    if (!message || !isChartReady || !symbol) {
      return;
    }

    switch (message.M) {
      case CHART_MESSAGE_TYPE.MOVE:
        /** Remove for now as the socket might be delay in result to the incorrect formation of candlestick */
        // this.generateNewBarData();
        break;
      case CHART_MESSAGE_TYPE.PATCH:
        this.patchChartData({
          low: message.D.L,
          high: message.D.H,
          open: message.D.O,
          close: message.D.C,
          ask: message.D.A,
          time: (message.D.T / 1000) as Time
        });
        break;
      default:
        break;
    }
  };

  private patchChartData = (dataItem: ICustomBarData) => {
    const { barData } = this;

    const newBarData = [...barData];

    for (let i = newBarData.length - 1; i > -1; i--) {
      const currItem = newBarData[i];

      if (currItem.time === dataItem.time) {
        const { ask, open, close, high, low } = destructureAsNumber(currItem, ['ask', 'open', 'close', 'high', 'low']);
        const { ask: newAsk, open: newOpen, close: newClose, high: newHigh, low: newLow } = dataItem;
        const replaceBarData = {
          ...dataItem,
          ask: newAsk ?? ask,
          open: newOpen ?? open,
          close: newClose ?? close,
          high: newHigh ?? high,
          low: newLow ?? low
        };
        newBarData.splice(i, 1, replaceBarData);
        break;
      }
    }
    this.barData = newBarData;
    this.dispatchEvent({
      type: EEventMessageType.CHART_PATCH,
      data: newBarData
    });
  };

  private generateNewBarData = (newDataItem: ICustomBarData) => {
    const { selectedPeriod } = equityChartService.context;
    const { barData } = this;

    const newBarData = [...barData];

    // Use new bid from the socket message as close of the new candlestick
    const { ask, close } = destructureAsNumber(newDataItem, ['ask', 'close']);

    const latest = newBarData[newBarData.length - 1];
    const latestTime = latest.time as number;
    const seconds = periodToSeconds(selectedPeriod);
    const newTime = (Math.floor(Math.floor(newDataItem.time as number) / seconds) * seconds) as Time; // to round down to the nearest timestamp

    const maxCurrentCandleTime = (latestTime + seconds) as Time;

    let nextCandleTime = maxCurrentCandleTime;

    if (newTime >= maxCurrentCandleTime) {
      nextCandleTime = newTime;
    }

    newBarData.push({
      open: close,
      close: close,
      low: close,
      high: close,
      ask: ask,
      time: nextCandleTime
    });

    this.barData = newBarData;

    this.dispatchEvent({
      type: EEventMessageType.CHART_FEED,
      data: newBarData
    });

    equityChartService.setUseNewDataAsOpen(true);
  };

  // Set as public for usage in useSymbolTopic
  public massageBarData = (newDataItem: ICustomBarData) => {
    const { selectedPeriod, useNewDataAsOpen, selectedProvider } = equityChartService.context;
    const { barData } = this;
    const { symbol } = this.selectedSymbol || {};
    const { isChartReady } = this;

    if (selectedProvider === EChartProvider.TRADING_VIEW) {
      // For TradingView, we don't need to massage the data
      this.dispatchEvent({
        type: EEventMessageType.SYMBOL_FEED,
        data: newDataItem
      });
      return;
    }

    if (isChartReady && symbol && barData.length > 0) {
      const newBarData = [...barData];
      const latest = newBarData.length > 0 ? newBarData[newBarData.length - 1] : newDataItem;
      const {
        ask: newAsk,
        open: newOpen,
        close: newClose
      } = destructureAsNumber(newDataItem, ['ask', 'open', 'close']);
      const { open: lastOpen, low: lastLow, high: lastHigh } = destructureAsNumber(latest, ['open', 'low', 'high']);

      const latestTime = latest.time as number;
      const seconds = periodToSeconds(selectedPeriod);
      // Max time for current candle
      const maxCurrentCandleTime = (latestTime + seconds) as Time;

      const isNextCandle = newDataItem.time >= maxCurrentCandleTime;
      if (isNextCandle) {
        this.generateNewBarData(newDataItem);
        return;
      }

      const finalData = {
        time: latest.time,
        ask: newAsk,
        open: useNewDataAsOpen ? newOpen : lastOpen,
        close: newClose,
        low: newClose < lastLow ? newClose : lastLow,
        high: newClose > lastHigh ? newClose : lastHigh
      };

      equityChartService.setUseNewDataAsOpen(false);

      if (newBarData.length === 0) {
        finalData.low = newClose;
        finalData.high = newClose;
        newBarData.push(finalData);
      } else {
        newBarData.splice(newBarData.length - 1, 1, finalData);
      }

      this.barData = [...newBarData];

      // Prevent sending ticks to the chart before data is ready
      if (!this.isChartLoading) {
        this.dispatchEvent({
          type: EEventMessageType.SYMBOL_FEED,
          data: finalData
        });
      }
    }
  };

  public dispatchUpdateIndicatorEvent = (event: IPostEventUpdateIndicatorFormMessage) => {
    this.dispatchEvent(event);
  };

  public resetChart = () => {
    this.cleanAllOrderTrackers();
    this.onSelectPeriod(EChartPeriod.PERIOD_H1);
    this.resetIndicator();
    this.resetAskPrice();
    this.dispatchEvent({
      type: EEventMessageType.RESET
    });
  };

  public resizeChart = () => {
    this.dispatchEvent({
      type: EEventMessageType.RESIZE
    });
  };

  public updateChartTheme = (theme: EAppThemeMode) => {
    if (this.checkSelectedProvider(EChartProvider.ORIGINAL)) {
      this.setOriginalChartTheme();
    } else {
      this.setTVTheme(theme);
    }
  };

  private setOriginalChartTheme = () => {
    const currentTheme = getThemeColors();
    const { positive, negative } = equityChartService.context.candlestickPreference;

    const data: IAppThemeColors = {
      ...currentTheme,
      candlestick: {
        ...currentTheme.candlestick,
        positive,
        negative
      }
    };
    this.dispatchEvent({
      type: EEventMessageType.THEME_SCHEME,
      data
    });
  };

  public getEntryAndAdditionalPrices = ({
    orderType,
    price,
    priceTrigger
  }: IGetEntryAndAdditionalPricesParams): {
    entryPrice: number;
    additionalPrice?: number;
  } => {
    switch (orderType) {
      case EOrderType.BUY_STOP_LIMIT:
      case EOrderType.SELL_STOP_LIMIT:
        return {
          entryPrice: priceTrigger,
          additionalPrice: price
        };

      default:
        return {
          entryPrice: price,
          additionalPrice: undefined
        };
    }
  };

  private getEntryPriceInfo = ({ orderType, decimal, entryPrice, additionalPrice }: IGetEntryPriceInfoParams) => {
    const orderTypeText = mapOrderTypeToText(orderType);
    const integration = companyService.getCompanyIntegration();
    const isCentroid = integration === EMainProductCodeType.CENTROID;

    const priceEntry = trimToDecimal(entryPrice, decimal) ?? 0;
    const priceAdditional = trimToDecimal(additionalPrice, decimal) ?? 0;

    if (isCentroid) {
      const formatStr = checkIsOpenPosition(orderType)
        ? `${i18n.t('Avg. Filled')} @ {price}`
        : `${orderTypeText} {orderId} @ {price}`;

      return [{ formatStr, price: priceEntry }];
    }

    const isStopLimit = orderType === EOrderType.BUY_STOP_LIMIT || orderType === EOrderType.SELL_STOP_LIMIT;

    if (isStopLimit) {
      return [
        {
          formatStr: `${orderTypeText} {orderId} | ${i18n.t('Stop Limit Price')} @ {price}`,
          price: priceEntry
        },
        {
          formatStr: `${orderTypeText} {orderId} @ {price}`,
          price: priceAdditional
        }
      ];
    }

    return [
      {
        formatStr: `${orderTypeText} {orderId} @ {price}`,
        price: priceEntry
      }
    ];
  };

  // ===== Order Tracking =====

  public trackOrder = (params: IOrderTracker, addToTrackers = true) => {
    const {
      symbol,
      decimal,
      orderId,
      orderType,
      stopLoss,
      takeProfit,
      entryPrice,
      additionalPrice,
      isClosable,
      isEditable
    } = params;

    const orderTrackers = this.orderTrackers;
    const isDiffSymbol = orderTrackers.length > 0 && orderTrackers.some((tracker) => tracker.symbol !== symbol);

    if (isDiffSymbol) {
      this.cleanAllOrderTrackers();
    }

    if (addToTrackers) {
      equityChartService.setOrderTrackers([
        ...this.orderTrackers,
        {
          symbol,
          orderId,
          decimal,
          orderType,
          stopLoss,
          takeProfit,
          entryPrice,
          isEditable,
          isClosable
        }
      ]);
    }

    this.dispatchEvent({
      type: EEventMessageType.TRACK_ORDER,
      data: {
        symbol,
        decimal,
        orderId,
        stopLoss: trimToDecimal(stopLoss, decimal) ?? 0,
        takeProfit: trimToDecimal(takeProfit, decimal) ?? 0,
        entryPriceInfo: this.getEntryPriceInfo({ orderType, decimal, entryPrice, additionalPrice }),
        isPendingOrder: !checkIsOpenPosition(orderType),
        buySellType: getOrderBuySellType(orderType),
        isEditable,
        isClosable
      }
    });
  };

  public cancelTrackOrder = (orderId: string) => {
    equityChartService.setOrderTrackers(this.orderTrackers.filter((tracker) => tracker.orderId !== orderId));

    this.dispatchEvent({
      type: EEventMessageType.CANCEL_TRACKER_ORDER,
      data: orderId
    });
  };

  public batchCancelTrackOrders = (orderIds: Array<IOrderTracker['orderId']>) => {
    this.dispatchEvent({
      type: EEventMessageType.BATCH_CANCEL_TRACKER_ORDER,
      data: orderIds
    });
  };

  private identifyAndReTrackOrder = (order: IOrder | IPendingOrder) => {
    switch (order.orderType) {
      case EOrderType.BUY:
      case EOrderType.SELL:
        {
          const {
            symbol,
            decimal,
            orderId,
            orderType,
            stopLoss,
            takeProfit,
            openPrice,
            closable,
            editable,
            marketFillPolicy
          } = order as IOrder;
          this.trackOrder(
            {
              symbol,
              decimal,
              orderId,
              orderType,
              stopLoss: safetyCheckForNumberValues(stopLoss),
              takeProfit: safetyCheckForNumberValues(takeProfit),
              entryPrice: safetyCheckForNumberValues(openPrice),
              isClosable: isOrderClosableOrEditable({ marketFillPolicy, flag: closable }),
              isEditable: isOrderClosableOrEditable({ marketFillPolicy, flag: editable })
            },
            false
          );
        }
        break;
      case EOrderType.BUY_LIMIT:
      case EOrderType.BUY_STOP:
      case EOrderType.BUY_STOP_LIMIT:
      case EOrderType.SELL_LIMIT:
      case EOrderType.SELL_STOP:
      case EOrderType.SELL_STOP_LIMIT:
        {
          const {
            symbol,
            decimal,
            orderId,
            orderType,
            stopLoss,
            takeProfit,
            price,
            priceTrigger,
            closable,
            editable,
            marketFillPolicy
          } = order as IPendingOrder;

          const { entryPrice, additionalPrice } = this.getEntryAndAdditionalPrices({
            orderType,
            price,
            priceTrigger
          });

          this.trackOrder(
            {
              symbol,
              decimal,
              orderId,
              orderType,
              stopLoss: safetyCheckForNumberValues(stopLoss),
              takeProfit: safetyCheckForNumberValues(takeProfit),
              entryPrice: entryPrice,
              additionalPrice: additionalPrice,
              isClosable: isOrderClosableOrEditable({ marketFillPolicy, flag: closable }),
              isEditable: isOrderClosableOrEditable({ marketFillPolicy, flag: editable })
            },
            false
          );
        }
        break;
    }
  };

  public retrackOrders = ({ orders, pendingOrders, updateTrackers }: IRetrackOrdersParams) => {
    const allTrackedOrderIds = this.orderTrackers.map(({ orderId }) => orderId);

    if (allTrackedOrderIds.length === 0) {
      return;
    }

    this.batchCancelTrackOrders(allTrackedOrderIds);

    const trackedOrders: Record<string, IOrder | IPendingOrder> = {};
    for (const order of orders) {
      const { orderId } = order;
      if (allTrackedOrderIds.includes(orderId)) {
        trackedOrders[orderId] = order;
      }
    }
    for (const pendingOrder of pendingOrders) {
      const { orderId } = pendingOrder;
      if (allTrackedOrderIds.includes(orderId)) {
        trackedOrders[orderId] = pendingOrder;
      }
    }

    const newOrderTrackers = equityChartService.context.orderTrackers.filter(({ orderId }) => orderId in trackedOrders);

    if (updateTrackers) {
      equityChartService.setOrderTrackers(newOrderTrackers);
    }

    for (const { orderId } of newOrderTrackers) {
      const trackedOrder = trackedOrders[orderId];
      if (trackedOrder) {
        this.identifyAndReTrackOrder(trackedOrder);
      }
    }
  };

  private updateTrackOrderImpl = () => {
    this.retrackOrders({
      orders: Object.values(orderListingService.context.mappedOpenPositions),
      pendingOrders: Object.values(orderListingService.context.mappedPendingOrders),
      updateTrackers: true
    });
  };

  public updateOrderTrackers = debounce(this.updateTrackOrderImpl, 500);

  public setPendingCloseOrderTrackerId = (orderId: string) => {
    equityChartService.setPendingCloseOrderTrackerId(orderId);
  };

  public flushPendingCloseOrderTrackerId = (orderDetail: Partial<INotificationMetadata>) => {
    const { pendingCloseOrderTrackerId } = equityChartService.context;
    const { centroidNotificationOrderState } = orderDetail;

    const shouldCancel = centroidNotificationOrderState !== ENotificationOrderStateCentroid.REJECTED;
    if (shouldCancel && pendingCloseOrderTrackerId) {
      this.cancelTrackOrder(pendingCloseOrderTrackerId);
    }
    equityChartService.setPendingCloseOrderTrackerId(undefined);
  };

  // ===== Candlestick Setting =====

  public initCandlestickPreference = async () => {
    await this.getCandlestickColorUserPreference();
    const { positive, negative } = equityChartService.context.candlestickPreference;
    this.dispatchCandlestickPreferenceEvent({ positive, negative });
  };

  public updateCandlestickColorUserPreference = async (params: {
    value: ICandlestickColorUserPreference;
    onSuccess?: () => void;
    onError?: (message: string) => void;
    sameAsDefault?: boolean;
  }) => {
    const { value, onSuccess, onError, sameAsDefault } = params;
    equityChartService.setIsCandlestickUpdating(true);
    const updateValue = sameAsDefault ? JSON.stringify({}) : JSON.stringify(value);
    await userAccountSettingAPIController.updateUserPreference(
      {
        category: EUserPreferenceCategory.CANDLESTICK,
        type: EUserPreferenceType.CANDLESTICK_COLOR,
        value: updateValue
      },
      {
        onSuccess: () => {
          this.dispatchCandlestickPreferenceEvent(value);
          onSuccess?.();
        },
        onError: (error) => {
          const { code, message } = error as IApiError;
          onError?.(mapResponseCodeToMessage(code, message));
        },
        onFinally: () => {
          equityChartService.setIsCandlestickUpdating(false);
        }
      }
    );
  };

  public getCandlestickColorUserPreference = async (shouldLoading = true) => {
    equityChartService.setIsCandlestickFetching(shouldLoading);
    await userAccountSettingAPIController.getUserPreference(
      {
        type: EUserPreferenceType.CANDLESTICK_COLOR,
        category: EUserPreferenceCategory.CANDLESTICK
      },
      {
        onSuccess: (result) => {
          const candlestickColorUserPreference = result?.length ? JSON.parse(result[0].value) : undefined;
          const { positive, negative } = candlestickColorUserPreference || {};

          this.setCandlestickPreferenceOrDefault({ positive, negative });
        },
        onError: (error) => {
          const { code, message } = error as IApiError;
          throw new Error(mapResponseCodeToMessage(code, message));
        },
        onFinally: () => {
          equityChartService.setIsCandlestickFetching(false);
        }
      }
    );
  };

  private setCandlestickPreferenceOrDefault = (data: ICandlestickColorUserPreference) => {
    const { profitColourCode, lossColourCode } = companyService.context.companyProfile || {};
    equityChartService.setCandlestickPreference({
      positive: data.positive || profitColourCode || DEFAULT_CANDLE_STICK_COLORS_RGB.positive,
      negative: data.negative || lossColourCode || DEFAULT_CANDLE_STICK_COLORS_RGB.negative
    });
  };

  private dispatchCandlestickPreferenceEvent = (data: ICandlestickColorUserPreference) => {
    this.dispatchEvent({
      type: EEventMessageType.UPDATE_CANDLESTICK_COLOR,
      data
    });
  };

  // Socket
  public initSocketReconnectionListener = () => {
    socketService.addReconnectListener(this.onReconnectSocket);
  };

  public removeSocketReconnectionListener = () => {
    socketService.removeReconnectListener(this.onReconnectSocket);
  };

  private onReconnectSocket = () => {
    if (this.selectedSymbol) {
      this.setSymbol(this.selectedSymbol);
    }
  };
}

export const equityChartViewController = new EquityChartViewController();
